/**
 * Mobile Home Page JavaScript
 * Handles zone selector, category filters, promo carousel, and interactions
 * Only active on mobile (max-width: 767px)
 */

(function () {
  'use strict';

  // Only run on mobile
  const isMobile = () => window.innerWidth <= 767;

  /**
   * Zone Selector Module
   */
  const ZoneSelector = {
    modal: null,
    triggers: [],
    activeTrigger: null,
    valueDisplay: null,
    input: null,
    zoneButtons: null,
    saveButton: null,
    sheet: null,
    zoneMap: null,
    selectedZone: null,
    selectedZoneFromMap: false,
    statusMessage: null,
    isLookingUp: false,
    sheetDragStartY: 0,
    sheetDragCurrentY: 0,
    sheetDragEligible: false,
    sheetDragActive: false,

    init() {
      this.modal = document.querySelector('[data-zone-modal]');
      this.triggers = Array.from(document.querySelectorAll('[data-zone-trigger]'));
      this.valueDisplay = document.querySelector('[data-zone-value]');
      this.input = document.querySelector('[data-zone-input]');
      this.saveButton = document.querySelector('[data-zone-save]');

      if (!this.modal || !this.triggers.length) return;
      this.sheet = this.modal.querySelector('.mh-zone-modal__content');
      this.zoneMap =
        window.MrMapleZonePickerMap &&
        window.MrMapleZonePickerMap.init(this.modal.querySelector('[data-zone-picker-map]'), {
          onSelect: (zone) => this.applyMapZoneSelection(zone),
        });
      if (this.zoneMap) {
        const warmMap = () => this.zoneMap && this.zoneMap.load();
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(warmMap, { timeout: 2500 });
        } else {
          window.setTimeout(warmMap, 1200);
        }
      }

      // Create status message element if it doesn't exist
      this.createStatusMessage();

      // Load saved zone from localStorage or Firebase
      this.loadSavedZone();

      // Also reload zone when auth becomes ready
      window.addEventListener(
        'garden:auth-ready',
        () => {
          this.loadSavedZone();
        },
        { once: true },
      );

      // Event listeners
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => this.openModal(trigger));
      });

      const backdrop = this.modal.querySelector('[data-zone-modal-close]');
      if (backdrop) {
        backdrop.addEventListener('click', () => this.closeModal());
      }

      if (this.saveButton) {
        this.saveButton.addEventListener('click', () => this.handleSave());
      }

      if (this.input) {
        this.input.addEventListener('input', (e) => {
          let value = e.target.value.trim();
          if (/^\d+$/.test(value)) {
            value = value.slice(0, 5);
            e.target.value = value;
          }

          this.selectedZone = null;
          this.selectedZoneFromMap = false;
          if (this.zoneMap) {
            this.zoneMap.setSelectedZone(null);
          }
          if (this.saveButton) {
            this.saveButton.textContent = 'Save zone';
          }
          this.clearStatus();
        });

        // Handle enter key to trigger ZIP lookup or save
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.handleSave();
          }
        });
      }

      this.bindSheetDismissGesture();

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('mh-zone-modal--open')) {
          this.closeModal();
        }
      });
    },

    createStatusMessage() {
      const inputWrapper = this.modal?.querySelector('.mh-zone-modal__input-wrapper');
      if (inputWrapper && !inputWrapper.querySelector('.mh-zone-modal__status')) {
        const status = document.createElement('div');
        status.className = 'mh-zone-modal__status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        inputWrapper.appendChild(status);
        this.statusMessage = status;
      }
    },

    bindSheetDismissGesture() {
      if (!this.sheet) return;

      this.sheet.addEventListener(
        'touchstart',
        (event) => {
          if (!this.modal.classList.contains('mh-zone-modal--open') || event.touches.length !== 1) {
            return;
          }

          const target = event.target;
          const interactiveTarget = target instanceof Element ? target.closest('a, button, input, select, textarea, [role="button"], [tabindex]') : null;

          if (interactiveTarget) {
            this.sheetDragEligible = false;
            return;
          }

          this.sheetDragStartY = event.touches[0].clientY;
          this.sheetDragCurrentY = this.sheetDragStartY;
          this.sheetDragEligible = this.sheet.scrollTop <= 0;
          this.sheetDragActive = false;
        },
        { passive: true },
      );

      this.sheet.addEventListener(
        'touchmove',
        (event) => {
          if (!this.sheetDragEligible || event.touches.length !== 1) {
            return;
          }

          this.sheetDragCurrentY = event.touches[0].clientY;
          const dragOffset = this.sheetDragCurrentY - this.sheetDragStartY;

          if (dragOffset <= 0 || this.sheet.scrollTop > 0) {
            return;
          }

          if (dragOffset > 8) {
            this.sheetDragActive = true;
            event.preventDefault();
            this.sheet.style.transition = 'none';
            this.sheet.style.transform = `translateY(${Math.min(dragOffset, window.innerHeight * 0.5)}px)`;
          }
        },
        { passive: false },
      );

      this.sheet.addEventListener('touchend', () => this.finishSheetDismissGesture());
      this.sheet.addEventListener('touchcancel', () => this.resetSheetDismissGesture());
    },

    finishSheetDismissGesture() {
      if (!this.sheetDragEligible) {
        return;
      }

      const dragOffset = this.sheetDragCurrentY - this.sheetDragStartY;
      const shouldDismiss = this.sheetDragActive && dragOffset > 88;

      if (shouldDismiss) {
        this.closeModal();
      } else {
        this.resetSheetDismissGesture({ animate: this.sheetDragActive });
      }
    },

    resetSheetDismissGesture(options = {}) {
      if (this.sheet) {
        this.sheet.style.transition = options.animate ? 'transform 0.18s ease' : '';
        this.sheet.style.transform = '';
        if (options.animate) {
          window.setTimeout(() => {
            if (this.sheet) {
              this.sheet.style.transition = '';
            }
          }, 180);
        }
      }
      this.sheetDragStartY = 0;
      this.sheetDragCurrentY = 0;
      this.sheetDragEligible = false;
      this.sheetDragActive = false;
    },

    setStatus(message, type = 'info') {
      if (this.statusMessage) {
        this.statusMessage.textContent = message;
        this.statusMessage.className = `mh-zone-modal__status mh-zone-modal__status--${type}`;
        this.statusMessage.style.display = 'block';
      }
    },

    clearStatus() {
      if (this.statusMessage) {
        this.statusMessage.textContent = '';
        this.statusMessage.style.display = 'none';
      }
    },

    applyMapZoneSelection(zone) {
      const normalized = String(zone || '')
        .trim()
        .toLowerCase();
      if (!normalized) return;

      this.selectedZone = normalized;
      this.selectedZoneFromMap = true;
      if (this.input) {
        this.input.value = '';
      }
      if (this.zoneMap) {
        this.zoneMap.setSelectedZone(normalized);
      }
      if (this.saveButton) {
        this.saveButton.disabled = false;
        this.saveButton.textContent = `Use ${this.formatZone(normalized)}`;
      }
      this.setStatus(`Use ${this.formatZone(normalized)}, or enter your ZIP code to find your exact zone.`, 'success');
    },

    ZONE_CACHE_TTL: 30 * 60 * 1000,

    _readCachedZone() {
      try {
        const z = localStorage.getItem('usdaZone') || localStorage.getItem('mrmaple_zone');
        return z ? String(z).toLowerCase() : null;
      } catch (e) {
        return null;
      }
    },

    _applyZone(zone) {
      const normalized = String(zone).toLowerCase();
      this.selectedZone = normalized;
      this.selectedZoneFromMap = false;
      if (this.valueDisplay) {
        this.valueDisplay.textContent = this.formatZone(normalized);
      }
      if (this.zoneMap) {
        this.zoneMap.setSelectedZone(normalized);
      }
      try {
        window.dispatchEvent(
          new CustomEvent('garden:zone-updated', {
            detail: { zone: normalized },
          }),
        );
      } catch (e) {}
    },

    async loadSavedZone() {
      const cachedZone = this._readCachedZone();
      if (cachedZone) {
        this._applyZone(cachedZone);
      }

      const isLoggedIn = !!(window.ShopifyCustomer?.id || (window.FirebaseAuthManager && window.FirebaseAuthManager.isAuthenticated()));

      if (!isLoggedIn) {
        if (!cachedZone && this.valueDisplay) {
          this.valueDisplay.textContent = 'Set Zone';
        }
        return;
      }

      const lastFetch = parseInt(localStorage.getItem('usdaZone_ts') || '0', 10);
      if (cachedZone && Date.now() - lastFetch < this.ZONE_CACHE_TTL) {
        return;
      }

      try {
        const avatarCache = window.MrMapleProfileAvatar || null;
        const data = window.MrMapleGardenProfileRequest
          ? await window.MrMapleGardenProfileRequest.get()
          : await fetch('/apps/garden/v1/profile', {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            }).then((response) => (response.ok ? response.json() : null));

        if (data) {
          if (data.success && data.profile) {
            if (avatarCache) {
              avatarCache.syncFromProfile(data.profile);
            }
            if (data.profile.zone) {
              const profileZone = String(data.profile.zone).toLowerCase();
              this._applyZone(profileZone);
              try {
                localStorage.setItem('usdaZone', profileZone);
                localStorage.setItem('usdaZone_ts', String(Date.now()));
              } catch (e) {}
            }
            if (!avatarCache && data.profile.profileImageUrl) {
              document.querySelectorAll('[data-mobile-avatar]').forEach(function (el) {
                const img = el.querySelector('.mh-avatar__img');
                if (img) {
                  img.src = data.profile.profileImageUrl;
                  el.classList.add('mh-avatar--has-image');
                }
              });
            }
            if (data.profile.zone) return;
          }
        }
      } catch (e) {
        console.warn('Failed to load zone from profile:', e);
      }

      if (!this.selectedZone && this.valueDisplay) {
        this.valueDisplay.textContent = 'Set Zone';
      }
    },

    openModal(trigger = null) {
      this.activeTrigger = trigger || this.activeTrigger || this.triggers[0] || null;
      this.resetSheetDismissGesture();
      this.modal.classList.add('mh-zone-modal--open');
      this.modal.setAttribute('aria-hidden', 'false');
      this.triggers.forEach((zoneTrigger) => zoneTrigger.setAttribute('aria-expanded', 'true'));
      document.body.style.overflow = 'hidden';
      if (this.zoneMap) {
        this.zoneMap.load();
      }
      this.prefillZipInput();
      this.clearStatus();

      if (this.input) {
        setTimeout(() => this.input.focus(), 100);
      }
    },

    prefillZipInput() {
      const zipApi = window.MrMapleZipClimate;
      const suggestedZip = zipApi && typeof zipApi.getSuggestedZip === 'function' ? zipApi.getSuggestedZip() : '';
      if (this.input && suggestedZip) {
        this.input.value = suggestedZip;
      }
    },

    closeModal() {
      this.resetSheetDismissGesture();
      this.modal.classList.remove('mh-zone-modal--open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.triggers.forEach((zoneTrigger) => zoneTrigger.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
      if (this.activeTrigger && typeof this.activeTrigger.focus === 'function') {
        this.activeTrigger.focus();
      }
    },

    isValidZipCode(value) {
      return /^\d{5}$/.test(value);
    },

    normalizeZip(value) {
      const digits = String(value == null ? '' : value).replace(/\D/g, '');
      if (!digits) return '';
      return digits.slice(-5).padStart(5, '0');
    },

    formatZone(zone) {
      return String(zone || '').toLowerCase();
    },

    async handleSave() {
      const inputValue = this.input?.value.trim() || '';

      if (!this.isValidZipCode(inputValue)) {
        if (this.selectedZoneFromMap && this.selectedZone) {
          if (this.saveButton) {
            this.saveButton.disabled = true;
            this.saveButton.textContent = 'Saving...';
          }
          try {
            await this.saveZone(this.selectedZone);
          } catch (error) {
            console.error('Zone save failed:', error);
            this.setStatus('We could not save that zone. Please try again.', 'error');
          } finally {
            this.selectedZoneFromMap = false;
            if (this.saveButton) {
              this.saveButton.disabled = false;
              this.saveButton.textContent = 'Save zone';
            }
          }
          return;
        }

        this.setStatus('Please enter a valid 5-digit ZIP code.', 'error');
        return;
      }

      await this.lookupZoneFromZip(inputValue);
    },

    async lookupZoneFromZip(zipCode) {
      if (this.isLookingUp) return;

      this.isLookingUp = true;
      this.setStatus('Looking up your USDA zone and heat profile...', 'info');

      if (this.saveButton) {
        this.saveButton.disabled = true;
        this.saveButton.textContent = 'Saving...';
      }

      try {
        const normalizedZip = this.normalizeZip(zipCode);
        const zipApi = window.MrMapleZipClimate;

        if (zipApi && typeof zipApi.lookupZipClimate === 'function') {
          const result = await zipApi.lookupZipClimate(normalizedZip);
          const zone = result.zone || this.selectedZone;

          if (!zone) {
            this.setStatus('Zone not found for that ZIP code. Please try again.', 'error');
            return;
          }

          if (this.input) {
            this.input.value = normalizedZip;
          }

          await this.saveZone(zone);
          this.setStatus(`Saved ZIP ${normalizedZip} for USDA Zone ${this.formatZone(zone)} and summer heat guidance.`, 'success');
          return;
        }

        const response = await fetch('https://cdn.shopify.com/s/files/1/1920/9319/files/filtered_csvjson.json');

        if (!response.ok) throw new Error('Failed to load zone data');

        const data = await response.json();
        const zoneData = Array.isArray(data) ? data.find((e) => this.normalizeZip(e?.zipcode) === normalizedZip) : null;

        if (zoneData && zoneData.zone) {
          const zone = String(zoneData.zone).toLowerCase();
          if (this.input) {
            this.input.value = normalizedZip;
          }
          await this.saveZone(zone);
        } else {
          this.setStatus('Zone not found for that ZIP code. Please try again.', 'error');
        }
      } catch (error) {
        console.error('Error fetching zone data:', error);
        this.setStatus('Error looking up zone. Please try again.', 'error');
      } finally {
        this.isLookingUp = false;
        if (this.saveButton) {
          this.saveButton.disabled = false;
          this.saveButton.textContent = 'Save zone';
        }
      }
    },

    async saveZone(zone) {
      if (!zone) return;
      const normalizedZone = String(zone).toLowerCase();

      localStorage.setItem('usdaZone', normalizedZone);
      localStorage.setItem('mrmaple_zone', normalizedZone);
      localStorage.setItem('usdaZone_ts', String(Date.now()));

      if (this.valueDisplay) {
        this.valueDisplay.textContent = this.formatZone(normalizedZone);
      }

      await this.saveZoneToServer(normalizedZone);

      try {
        window.dispatchEvent(
          new CustomEvent('garden:zone-updated', {
            detail: { zone: normalizedZone },
          }),
        );
      } catch (error) {
        console.warn('Failed to emit zone update event:', error);
      }

      this.closeModal();
    },

    async saveZoneToServer(zone) {
      // Save zone to Garden app profile if customer is logged in
      try {
        if (window.FirebaseAuthManager && window.FirebaseAuthManager.isAuthenticated()) {
          await fetch('/apps/garden/v1/profile', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              action: 'update-profile',
              updates: { zone: zone },
            }),
          });
        }
      } catch (error) {
        // Silently fail - localStorage backup is available
        console.warn('Zone save to server failed:', error);
      }
    },
  };

  /**
   * Promo Carousel Module
   */
  const PromoCarousel = {
    carousel: null,
    dots: null,
    cards: null,
    currentIndex: 0,

    init() {
      this.carousel = document.querySelector('[data-promo-carousel]');
      this.dots = document.querySelectorAll('[data-promo-dot]');

      if (!this.carousel) return;

      this.cards = this.carousel.querySelectorAll('[data-promo-card]');

      // Scroll event for dot indicators
      this.carousel.addEventListener(
        'scroll',
        () => {
          this.updateDots();
        },
        { passive: true },
      );

      // Dot click handlers
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.scrollToCard(index);
        });
      });

      // Touch/swipe improvements
      this.carousel.addEventListener(
        'touchstart',
        () => {
          this.carousel.style.scrollSnapType = 'none';
        },
        { passive: true },
      );

      this.carousel.addEventListener(
        'touchend',
        () => {
          this.carousel.style.scrollSnapType = 'x mandatory';
        },
        { passive: true },
      );
    },

    updateDots() {
      if (!this.carousel || !this.dots.length) return;

      const scrollLeft = this.carousel.scrollLeft;
      const cardWidth = this.cards[0]?.offsetWidth || 0;
      const gap = 16; // var(--mh-spacing-md)

      const index = Math.round(scrollLeft / (cardWidth + gap));
      this.currentIndex = Math.min(index, this.cards.length - 1);

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('mh-promo__dot--active', i === this.currentIndex);
      });
    },

    scrollToCard(index) {
      if (!this.cards[index]) return;

      const cardWidth = this.cards[0].offsetWidth;
      const gap = 16;
      const scrollPosition = index * (cardWidth + gap);

      this.carousel.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    },
  };

  /**
   * Category Filter Module
   */
  const CategoryFilter = {
    pills: null,
    panels: null,
    grid: null,
    currentHandle: 'all',

    init() {
      this.pills = document.querySelectorAll('[data-category-pill]');
      this.panels = document.querySelectorAll('[data-products-panel]');
      this.grid = document.querySelector('[data-products-grid]');

      if (!this.pills.length || !this.panels.length) return;

      const activePill = Array.from(this.pills).find((pill) => pill.classList.contains('mh-category-pill--active')) || this.pills[0];
      this.currentHandle = activePill?.dataset.collectionHandle || this.currentHandle;
      this.showPanel(this.currentHandle);

      this.pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          this.selectCategory(pill);
        });
      });
    },

    selectCategory(pill) {
      const handle = pill.dataset.collectionHandle;

      // Update active state
      this.pills.forEach((p) => p.classList.remove('mh-category-pill--active'));
      pill.classList.add('mh-category-pill--active');

      if (handle !== this.currentHandle) {
        this.currentHandle = handle;
        this.showPanel(handle);
      }
    },

    showPanel(handle) {
      const panelList = Array.from(this.panels);
      const activePanel = panelList.find((panel) => panel.dataset.collectionHandle === handle) || panelList[0] || null;

      panelList.forEach((panel) => {
        const isActive = panel === activePanel;
        panel.hidden = !isActive;
        panel.classList.toggle('mh-products__panel--hidden', !isActive);
      });

      this.grid = activePanel ? activePanel.querySelector('.mh-products__grid') : null;

      if (activePanel) {
        loadLazyImages(activePanel);
      }
    },
  };

  /**
   * Wishlist Buttons Module
   */
  const WishlistButtons = {
    buttons: null,

    init() {
      this.bindScope(document);
    },

    bindScope(root) {
      if (!root || typeof root.querySelectorAll !== 'function') return;

      const buttons = Array.from(root.querySelectorAll('[data-wishlist-toggle]'));
      if (!buttons.length) {
        this.buttons = document.querySelectorAll('[data-wishlist-toggle]');
        return;
      }

      buttons.forEach((btn) => {
        if (btn.dataset.wishlistBound === 'true') return;
        btn.dataset.wishlistBound = 'true';
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleWishlist(btn);
        });
      });

      this.buttons = document.querySelectorAll('[data-wishlist-toggle]');
      this.loadSavedState(root);
    },

    loadSavedState(root = document) {
      const saved = JSON.parse(localStorage.getItem('mrmaple_wishlist') || '[]');
      const buttons = root.querySelectorAll ? root.querySelectorAll('[data-wishlist-toggle]') : this.buttons;

      Array.from(buttons || []).forEach((btn) => {
        const productId = btn.dataset.productId;
        if (saved.includes(productId)) {
          btn.classList.add('mh-product-card__wishlist--active');
          btn.setAttribute('aria-pressed', 'true');
          const svg = btn.querySelector('svg');
          if (svg) {
            svg.setAttribute('fill', 'currentColor');
          }
        }
      });
    },

    toggleWishlist(btn) {
      const productId = btn.dataset.productId;
      const isActive = btn.classList.contains('mh-product-card__wishlist--active');

      // Toggle state
      btn.classList.toggle('mh-product-card__wishlist--active');
      btn.setAttribute('aria-pressed', !isActive);

      const svg = btn.querySelector('svg');
      svg.setAttribute('fill', isActive ? 'none' : 'currentColor');

      // Update localStorage
      let saved = JSON.parse(localStorage.getItem('mrmaple_wishlist') || '[]');
      if (isActive) {
        saved = saved.filter((id) => id !== productId);
      } else {
        if (!saved.includes(productId)) {
          saved.push(productId);
        }
      }
      localStorage.setItem('mrmaple_wishlist', JSON.stringify(saved));

      // Sync with Garden app API
      this.syncWithServer(btn, !isActive);

      // Visual feedback
      this.animateHeart(btn);
    },

    animateHeart(btn) {
      btn.style.transform = 'scale(1.2)';
      setTimeout(() => {
        btn.style.transform = '';
      }, 200);
    },

    async syncWithServer(btn, isAdding) {
      const data = {
        productId: btn.dataset.productId,
        variantId: btn.dataset.variantId,
        handle: btn.dataset.productHandle,
      };

      try {
        const endpoint = isAdding ? '/apps/garden/v1/items' : `/apps/garden/v1/items/${data.variantId}`;
        const method = isAdding ? 'POST' : 'DELETE';

        await fetch(endpoint, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: isAdding ? JSON.stringify(data) : undefined,
        });
      } catch (error) {
        console.warn('Wishlist sync failed:', error);
      }
    },
  };

  /**
   * Personalized Recommendations Module
   * Hydrates the home "Recommended For You" grid from the Garden v2 engine,
   * keeps a larger local pool, and rotates the visible slice on refresh.
   */
  const PersonalizedRecommendations = {
    section: null,
    grid: null,
    link: null,
    refreshButton: null,
    fallbackProducts: [],
    currentPool: [],
    rotation: 0,
    lastDisplayHandles: [],
    cacheTtlMs: 15 * 60 * 1000,
    softTtlMs: 10 * 60 * 1000,
    forcedRefreshCoalesceMs: 10 * 1000,
    requestToken: 0,
    requestPromise: null,
    requestCacheKey: '',
    forcedRefreshCacheKey: '',
    forcedRefreshAt: 0,
    refreshDebounceTimer: null,
    initialRefreshStarted: false,
    initialRefreshObserver: null,

    init() {
      this.section = document.querySelector('[data-mobile-product-grid]');
      this.grid = this.section ? this.section.querySelector('[data-products-grid]') : null;
      this.link = this.section ? this.section.querySelector('[data-products-link]') : null;
      this.refreshButton = this.section ? this.section.querySelector('[data-products-refresh]') : null;

      if (!this.section || !this.grid || !document.body.classList.contains('template-index')) {
        return;
      }

      this.fallbackProducts = this.captureFallbackProducts();
      this.bindRefreshButton();
      this.scheduleInitialRefresh();

      window.addEventListener(
        'garden:auth-ready',
        () => {
          this.queueRefresh({ force: true });
        },
        { passive: true },
      );

      window.addEventListener(
        'garden:profile-updated',
        () => {
          this.queueRefresh({ force: true });
        },
        { passive: true },
      );

      window.addEventListener(
        'garden:zone-updated',
        () => {
          this.queueRefresh({ force: true });
        },
        { passive: true },
      );

      window.addEventListener('storage', (event) => {
        if (event.key === 'usdaZone' || event.key === 'mrmaple_zone') {
          this.queueRefresh({ force: true });
        }
      });
    },

    scheduleInitialRefresh() {
      if (this.initialRefreshStarted) return;

      const cacheKey = this.getCacheKey();
      const cached = this.readCache(cacheKey);
      if (this.payloadRecommendationCount(cached?.payload) > 0) {
        this.currentPool = this.buildPool(cached.payload.recommendations, cached.payload.fallbacks);
        void this.renderCurrentPool();
        if (Date.now() - (cached.fetchedAt || 0) < this.softTtlMs) {
          this.initialRefreshStarted = true;
          return;
        }
      }

      const start = () => {
        if (this.initialRefreshStarted) return;
        this.initialRefreshStarted = true;
        if (this.initialRefreshObserver) {
          this.initialRefreshObserver.disconnect();
          this.initialRefreshObserver = null;
        }
        void this.refresh();
      };

      if (this.section && typeof IntersectionObserver === 'function') {
        this.initialRefreshObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              start();
            }
          },
          { rootMargin: '360px 0px' },
        );
        this.initialRefreshObserver.observe(this.section);
      }

      const scheduleIdleStart = () => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(start, { timeout: 2800 });
          return;
        }
        window.setTimeout(start, 1600);
      };

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => window.requestAnimationFrame(scheduleIdleStart));
      } else {
        scheduleIdleStart();
      }
    },

    queueRefresh(options = {}) {
      if (this.refreshDebounceTimer) {
        window.clearTimeout(this.refreshDebounceTimer);
      }
      this.refreshDebounceTimer = window.setTimeout(() => {
        this.refreshDebounceTimer = null;
        void this.refresh(options);
      }, 150);
    },

    bindRefreshButton() {
      if (!this.refreshButton || this.refreshButton.__recommendationsBound) {
        return;
      }

      this.refreshButton.__recommendationsBound = true;
      this.refreshButton.dataset.defaultLabel = this.refreshButton.textContent?.trim() || 'Refresh';
      this.refreshButton.addEventListener('click', () => {
        if (this.currentPool.length > this.getLimit()) {
          this.rotation += 1;
          void this.renderCurrentPool();
          return;
        }

        this.rotation += 1;
        void this.refresh({ force: true });
      });
    },

    normalizeZone(value) {
      if (typeof value !== 'string') return null;
      const normalized = value.trim().toLowerCase();
      return normalized || null;
    },

    getCurrentZone() {
      try {
        return this.normalizeZone(localStorage.getItem('usdaZone') || localStorage.getItem('mrmaple_zone'));
      } catch (error) {
        return null;
      }
    },

    getCustomerKey() {
      return window.ShopifyCustomer?.id ? String(window.ShopifyCustomer.id) : 'anon';
    },

    getLimit() {
      const raw = this.section?.dataset.recommendationsLimit || '10';
      const parsed = parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
    },

    getPoolLimit() {
      const raw = this.section?.dataset.recommendationsPoolLimit || '';
      const parsed = parseInt(raw, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.max(this.getLimit(), Math.min(parsed, 20));
      }

      return Math.max(this.getLimit(), 20);
    },

    getStrategy() {
      return this.section?.dataset.recommendationsStrategy || 'personalized-home';
    },

    getEngine() {
      const engine = String(this.section?.dataset.recommendationsEngine || '')
        .trim()
        .toLowerCase();
      return engine === 'v1' || engine === 'v2' ? engine : 'v2';
    },

    getEndpoint() {
      return this.section?.dataset.recommendationsEndpoint || '/apps/garden/v1/recommendations';
    },

    getFallbackUrl() {
      return this.section?.dataset.recommendationsFallbackUrl || '/collections/all';
    },

    getCacheKey() {
      const zone = this.getCurrentZone() || 'no-zone';
      return `mrmaple:mobile-home-recommendations:v2:${this.getCustomerKey()}:${zone}:${this.getLimit()}:${this.getPoolLimit()}`;
    },

    payloadRecommendationCount(payload) {
      return (
        (Array.isArray(payload?.recommendations) ? payload.recommendations.length : 0) + (Array.isArray(payload?.fallbacks) ? payload.fallbacks.length : 0)
      );
    },

    ensureMemoryCache() {
      if (!window.__GardenRecommendationCache || typeof window.__GardenRecommendationCache !== 'object') {
        window.__GardenRecommendationCache = {};
      }

      return window.__GardenRecommendationCache;
    },

    readCache(cacheKey) {
      const now = Date.now();
      const memoryCache = this.ensureMemoryCache();
      const memoryEntry = memoryCache[cacheKey];
      if (memoryEntry && memoryEntry.expiresAt > now && memoryEntry.payload) {
        return memoryEntry;
      }

      try {
        const raw = localStorage.getItem(cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.expiresAt <= now || !parsed.payload) {
          localStorage.removeItem(cacheKey);
          return null;
        }
        memoryCache[cacheKey] = parsed;
        return parsed;
      } catch (error) {
        return null;
      }
    },

    writeCache(cacheKey, payload) {
      const entry = {
        fetchedAt: Date.now(),
        expiresAt: Date.now() + this.cacheTtlMs,
        payload,
      };

      this.ensureMemoryCache()[cacheKey] = entry;

      try {
        localStorage.setItem(cacheKey, JSON.stringify(entry));
      } catch (error) {
        console.warn('Unable to persist recommendation cache:', error);
      }
    },

    buildRequestUrl() {
      const url = new URL(this.getEndpoint(), window.location.origin);
      url.searchParams.set('strategy', this.getStrategy());
      url.searchParams.set('limit', String(this.getPoolLimit()));
      url.searchParams.set('engine', this.getEngine());

      const zone = this.getCurrentZone();
      if (zone) {
        url.searchParams.set('zone', zone);
      }

      return url.toString();
    },

    extractHandleFromUrl(value) {
      if (!value) return '';

      try {
        const url = new URL(String(value), window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/i);
        if (!match) return '';
        return decodeURIComponent(match[1]).trim();
      } catch (error) {
        return '';
      }
    },

    captureFallbackProducts() {
      if (!this.grid) return [];

      return Array.from(this.grid.querySelectorAll('[data-product-item]'))
        .map((card) => {
          const wishlistCard = card.querySelector('[data-wishlist-card]');
          const titleLink = card.querySelector('.productitem--title a, [data-product-page-link]');
          const imageLink = card.querySelector('.productitem--image-link, [data-product-page-link]');
          const imageEl = card.querySelector('.productitem--image-primary, .productitem--image img');
          const rawUrl = titleLink?.getAttribute('href') || imageLink?.getAttribute('href') || '';
          const rawPrice = wishlistCard?.dataset.productPrice || '';
          const parsedPrice = Number(rawPrice);

          return {
            productId: wishlistCard?.dataset.productId || '',
            variantId: wishlistCard?.dataset.variantId || '',
            handle: wishlistCard?.dataset.productHandle || this.extractHandleFromUrl(rawUrl),
            title: wishlistCard?.dataset.productTitle || titleLink?.textContent?.trim() || '',
            vendor: wishlistCard?.dataset.productVendor || '',
            image: wishlistCard?.dataset.productImage || imageEl?.getAttribute('src') || '',
            price: Number.isFinite(parsedPrice) ? parsedPrice : null,
            currency: wishlistCard?.dataset.productCurrency || 'USD',
            url: rawUrl,
          };
        })
        .filter((item) => item.handle && item.title && item.url);
    },

    normalizePoolItem(item, source) {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const handle = String(item.handle || this.extractHandleFromUrl(item.url)).trim();
      const productId = String(item.productId || item.productGid || '').trim();
      const variantId = String(item.variantId || '').trim();
      const url = item.url || (handle ? `/products/${encodeURIComponent(handle)}` : '');
      if (!handle || !url) {
        return null;
      }

      const rawPrice = item.price && typeof item.price === 'object' ? item.price.amount : item.price;
      const parsedPrice = Number(rawPrice);
      const numericPrice = Number.isFinite(parsedPrice) ? parsedPrice : null;
      const currencyCode = item.currency || (item.price && typeof item.price === 'object' && item.price.currencyCode) || 'USD';

      return {
        ...item,
        source,
        handle,
        productId,
        variantId,
        url,
        title: String(item.title || '').trim(),
        image: item.image || '',
        vendor: item.vendor || '',
        price: numericPrice,
        currency: currencyCode,
        reason: source === 'fallback' ? '' : String(item.reason || '').trim(),
      };
    },

    buildPool(recommendations, snapshotFallbacks) {
      const poolLimit = this.getPoolLimit();
      const pool = [];
      const seen = new Set();

      const pushItem = (item, source) => {
        const normalized = this.normalizePoolItem(item, source);
        if (!normalized) return;

        const dedupeKey = normalized.handle || normalized.productId;
        if (!dedupeKey || seen.has(dedupeKey)) return;

        seen.add(dedupeKey);
        pool.push(normalized);
      };

      (recommendations || []).forEach((item) => pushItem(item, 'personalized'));
      (snapshotFallbacks || []).forEach((item) => pushItem(item, 'snapshot-fallback'));
      this.fallbackProducts.forEach((item) => pushItem(item, 'fallback'));

      return pool.slice(0, poolLimit);
    },

    hashString(value) {
      const input = String(value || '');
      let hash = 2166136261;

      for (let index = 0; index < input.length; index += 1) {
        hash ^= input.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }

      return hash >>> 0;
    },

    shuffleWithSeed(items, seed) {
      const output = Array.isArray(items) ? items.slice() : [];
      let state = this.hashString(seed) || 1;

      const nextRandom = () => {
        state = Math.imul(state ^ (state >>> 15), 2246822519);
        state = Math.imul(state ^ (state >>> 13), 3266489917);
        state ^= state >>> 16;
        return (state >>> 0) / 4294967296;
      };

      for (let index = output.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(nextRandom() * (index + 1));
        const current = output[index];
        output[index] = output[swapIndex];
        output[swapIndex] = current;
      }

      return output;
    },

    sameHandleSet(items, handles) {
      if (!Array.isArray(items) || !Array.isArray(handles) || items.length !== handles.length) {
        return false;
      }

      const current = items
        .map((item) => String(item?.handle || '').trim())
        .filter(Boolean)
        .sort();
      const previous = handles
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .sort();

      if (current.length !== previous.length) {
        return false;
      }

      for (let index = 0; index < current.length; index += 1) {
        if (current[index] !== previous[index]) {
          return false;
        }
      }

      return true;
    },

    buildDisplayRecommendations(pool, rotationOffset = 0) {
      const limit = this.getLimit();
      const personalized = pool.filter((item) => item.source === 'personalized');
      const fallback = pool.filter((item) => item.source !== 'personalized');
      const previousHandles = this.lastDisplayHandles;
      const minimumFallback = fallback.length > 0 ? Math.min(3, fallback.length) : 0;
      const preferredPersonalizedCount = personalized.length > 0 ? Math.max(limit - minimumFallback, Math.ceil(limit * 0.7)) : 0;

      let selection = [];

      for (let attempt = 0; attempt < 4; attempt += 1) {
        const seedKey = `${this.getCustomerKey()}:${this.getCurrentZone() || 'no-zone'}:${this.rotation + rotationOffset + attempt}`;
        const primary = this.shuffleWithSeed(personalized, `${seedKey}:personalized`);
        const secondary = this.shuffleWithSeed(fallback, `${seedKey}:fallback`);
        const combined = [];
        const combinedHandles = new Set();

        const pushGroup = (items) => {
          items.forEach((item) => {
            const handle = String(item?.handle || '').trim();
            if (!handle || combinedHandles.has(handle) || combined.length >= limit) {
              return;
            }

            combinedHandles.add(handle);
            combined.push(item);
          });
        };

        pushGroup(primary.slice(0, preferredPersonalizedCount));
        pushGroup(secondary.slice(0, Math.max(0, limit - combined.length)));
        pushGroup(primary);
        pushGroup(secondary);

        const finalSelection = this.shuffleWithSeed(combined, `${seedKey}:display`).slice(0, limit);

        if (!this.sameHandleSet(finalSelection, previousHandles) || pool.length <= limit) {
          selection = finalSelection;
          break;
        }
      }

      return selection;
    },

    setRefreshBusy(isBusy) {
      if (!this.refreshButton) return;
      const defaultLabel = this.refreshButton.dataset.defaultLabel || 'Refresh';
      this.refreshButton.disabled = isBusy;
      this.refreshButton.textContent = isBusy ? 'Refreshing...' : defaultLabel;
    },

    emitImpression(products) {
      if (!Array.isArray(products) || !products.length || typeof CustomEvent !== 'function') {
        return;
      }

      document.dispatchEvent(
        new CustomEvent('mm:recommendations:impression', {
          bubbles: true,
          detail: {
            variant: 'home',
            recommendations: products
              .filter((item) => item && item.productId)
              .map((item) => ({
                productId: item.productId,
                handle: item.handle,
                reason: item.reason,
                reasonSource: item.source || null,
              })),
          },
        }),
      );
    },

    async renderCurrentPool() {
      if (!this.grid || !Array.isArray(this.currentPool) || this.currentPool.length === 0) {
        return;
      }

      const displayRecommendations = this.buildDisplayRecommendations(this.currentPool);
      if (!displayRecommendations.length) {
        return;
      }

      this.grid.classList.add('mh-products__grid--personalized');
      this.grid.innerHTML = displayRecommendations.map((item) => this.renderCard(item)).join('');
      this.lastDisplayHandles = displayRecommendations.map((item) => item.handle);
      this.emitImpression(displayRecommendations);

      if (this.link) {
        this.link.href = this.getFallbackUrl();
      }

      WishlistButtons.bindScope(this.grid);
    },

    async refresh(options = {}) {
      if (!this.section || !this.grid) return;

      const { force = false } = options;
      const cacheKey = this.getCacheKey();
      const cached = this.readCache(cacheKey);
      const now = Date.now();

      if (this.payloadRecommendationCount(cached?.payload) > 0) {
        this.currentPool = this.buildPool(cached.payload.recommendations, cached.payload.fallbacks);
        await this.renderCurrentPool();
        const freshEnough = now - (cached.fetchedAt || 0) < this.softTtlMs;
        if (!force && freshEnough) {
          return;
        }
        if (force && freshEnough && this.forcedRefreshCacheKey === cacheKey && now - this.forcedRefreshAt < this.forcedRefreshCoalesceMs) {
          return;
        }
      }

      if (this.requestPromise && this.requestCacheKey === cacheKey) {
        return this.requestPromise;
      }

      if (force && this.forcedRefreshCacheKey === cacheKey && now - this.forcedRefreshAt < this.forcedRefreshCoalesceMs) {
        return;
      }

      if (force) {
        this.forcedRefreshCacheKey = cacheKey;
        this.forcedRefreshAt = now;
      }

      const currentToken = ++this.requestToken;
      this.setRefreshBusy(true);

      const requestPromise = (async () => {
        try {
          const response = await fetch(this.buildRequestUrl(), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });

          if (currentToken !== this.requestToken) {
            return;
          }

          if (!response.ok) {
            throw new Error(`Recommendation request failed (${response.status})`);
          }

          const payload = await response.json();
          if (!payload || !Array.isArray(payload.recommendations)) {
            return;
          }

          this.writeCache(cacheKey, payload);
          this.currentPool = this.buildPool(payload.recommendations, payload.fallbacks);
          await this.renderCurrentPool();
        } catch (error) {
          console.warn('Personalized recommendations unavailable:', error);
          if (!this.currentPool.length) {
            this.currentPool = this.buildPool([], []);
            await this.renderCurrentPool();
          }
        } finally {
          if (currentToken === this.requestToken) {
            this.setRefreshBusy(false);
          }
        }
      })();

      this.requestCacheKey = cacheKey;
      this.requestPromise = requestPromise;

      try {
        return await requestPromise;
      } finally {
        if (this.requestPromise === requestPromise) {
          this.requestPromise = null;
          this.requestCacheKey = '';
        }
      }
    },

    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },

    formatMoney(amount, currencyCode) {
      const numericAmount = Number(amount);
      if (!Number.isFinite(numericAmount)) return '';

      const currency = currencyCode || 'USD';

      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
          maximumFractionDigits: numericAmount % 1 === 0 ? 0 : 2,
        }).format(numericAmount);
      } catch (error) {
        return `$${numericAmount.toFixed(2)}`;
      }
    },

    renderCard(item) {
      const rawTitle = (item.title || 'Recommended plant')
        .replace(/^(?:FOR\s+)?PICK\s*UP\s+ONLY\s*\|\s*/i, '')
        .replace(/\s*\|\s*DOES NOT SHIP\s*$/i, '')
        .replace(/DOES NOT SHIP\s*$/i, '')
        .trim();
      const title = this.escapeHtml(rawTitle);
      const vendor = item.vendor ? `<p class="mh-product-card__vendor">${this.escapeHtml(item.vendor)}</p>` : '';
      const reason = item.reason ? `<p class="mh-product-card__reason">${this.escapeHtml(item.reason)}</p>` : '';
      const image = item.image
        ? `<img class="mh-product-card__image" src="${this.escapeHtml(item.image)}" alt="${title}" loading="lazy">`
        : `<div class="mh-product-card__image-placeholder" aria-hidden="true">MR</div>`;
      const price = this.formatMoney(item.price, item.currency);
      const priceMarkup = price ? `<div class="mh-product-card__price">${this.escapeHtml(price)}</div>` : '';
      const productUrl = this.escapeHtml(item.url || `/products/${item.handle || ''}`);
      const handle = this.escapeHtml(item.handle || '');
      const productId = this.escapeHtml(item.productId || '');
      const variantId = this.escapeHtml(item.variantId || '');

      return `
        <article
          class="mh-product-card mh-product-card--personalized"
          data-mh-product-card
          ${productId ? `data-mm-rec-product-id="${productId}"` : ''}
          data-mm-rec-surface="home"
          ${handle ? `data-mm-rec-handle="${handle}"` : ''}
          ${item.source ? `data-rec-source="${this.escapeHtml(item.source)}"` : ''}
        >
          <div class="mh-product-card__image-wrapper">
            <a class="mh-product-card__image-link" href="${productUrl}">
              ${image}
            </a>
            <button
              class="mh-product-card__wishlist"
              type="button"
              data-wishlist-toggle
              data-product-id="${productId}"
              data-variant-id="${variantId}"
              data-product-handle="${handle}"
              aria-label="Save ${title} to wishlist"
              aria-pressed="false"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d="M12 20.5 4.9 13.7a4.8 4.8 0 0 1 6.8-6.8L12 7.2l.3-.3a4.8 4.8 0 1 1 6.8 6.8Z"></path>
              </svg>
            </button>
          </div>
          <div class="mh-product-card__info">
            ${reason}
            <a class="mh-product-card__title-link" href="${productUrl}">
              <h3 class="mh-product-card__title">${title}</h3>
            </a>
            ${vendor}
            ${priceMarkup}
            <a class="mh-product-card__cta" href="${productUrl}">Buy Now</a>
          </div>
        </article>
      `;
    },
  };

  /**
   * Search Handler Module
   * Integrates with Shopify's predictive search API
   */
  const PAST_SEARCH_KEY = 'mm_past_searches';
  const MAX_PAST_SEARCHES = 5;
  const MAX_RECENT_PRODUCTS = 6;
  const SEARCH_OPENING_GUARD_MS = 700;

  const SearchHandler = {
    input: null,
    flydown: null,
    resultsContainer: null,
    placeholder: null,
    quickLinks: null,
    cancelBtn: null,
    searchWrapper: null,
    historyContainer: null,
    recentContainer: null,
    debounceTimer: null,
    isOpen: false,
    isModalOpen: false,
    recentProductsRendered: false,
    ignoreOutsideClickUntil: 0,
    scrollTouchStartX: 0,
    scrollTouchStartY: 0,
    lastKeyboardDismissAt: 0,

    init() {
      this.input = document.querySelector('[data-mobile-search-input]');
      this.flydown = document.querySelector('[data-mobile-search-flydown]');
      this.resultsContainer = document.querySelector('[data-mobile-search-results]');
      this.placeholder = this.flydown ? this.flydown.querySelector('[data-mobile-search-placeholder]') : null;
      this.quickLinks = document.querySelector('[data-mobile-search-quicklinks]');
      this.cancelBtn = document.querySelector('[data-mobile-search-cancel]');
      this.searchWrapper = document.querySelector('[data-mobile-home-search]');
      this.historyContainer = document.querySelector('[data-mobile-search-history]');
      this.recentContainer = document.querySelector('[data-mobile-search-recent]');

      if (!this.input) return;

      this._bindKeyboardDismissOnSearchScroll();

      const activateFullscreenSearch = () => {
        if (!window.matchMedia('(max-width: 767px)').matches) return false;
        this.openModal();
        return true;
      };

      this.input.addEventListener('focus', () => {
        activateFullscreenSearch();
        const query = this.input.value.trim();
        if (query.length >= 2) {
          this.performPredictiveSearch(query);
        } else {
          this.showQuickLinks();
        }
      });

      this.input.addEventListener('input', (e) => {
        clearTimeout(this.debounceTimer);
        const query = e.target.value.trim();

        if (query.length < 2) {
          this.showQuickLinks();
          return;
        }

        this.debounceTimer = setTimeout(() => {
          this.performPredictiveSearch(query);
        }, 300);
      });

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === 'Return') {
          if (e.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
          const query = this.input.value.trim();
          if (query) {
            e.preventDefault();
            this._goToSearchResults(query);
            return;
          }
          e.preventDefault();
        }
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      var form = this.input.closest('form');
      if (form) {
        form.addEventListener('submit', () => {
          var q = this.input.value.trim();
          if (q) this._saveSearch(q);
        });
      }

      if (this.cancelBtn) {
        this.cancelBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeModal();
        });
      }

      document.addEventListener('click', (e) => {
        const target = e.target;
        if (!this.isModalOpen || !(target instanceof Element)) return;
        if (target.closest('[data-mobile-home-search]')) return;
        if (Date.now() < this.ignoreOutsideClickUntil) {
          return;
        }

        this.closeModal();
      });
    },

    _bindKeyboardDismissOnSearchScroll() {
      if (!this.flydown) return;

      const shouldWatch = () => {
        return Boolean(this.isModalOpen && this.input && document.activeElement === this.input);
      };

      this.flydown.addEventListener(
        'touchstart',
        (event) => {
          if (!shouldWatch() || !event.touches || !event.touches.length) return;
          this.scrollTouchStartX = event.touches[0].clientX;
          this.scrollTouchStartY = event.touches[0].clientY;
        },
        { passive: true },
      );

      this.flydown.addEventListener(
        'touchmove',
        (event) => {
          if (!shouldWatch() || !event.touches || !event.touches.length) return;
          var dx = Math.abs(event.touches[0].clientX - this.scrollTouchStartX);
          var dy = Math.abs(event.touches[0].clientY - this.scrollTouchStartY);
          if (Math.max(dx, dy) < 8) return;
          this._dismissKeyboardForSearchScroll();
        },
        { passive: true },
      );

      this.flydown.addEventListener(
        'wheel',
        () => {
          this._dismissKeyboardForSearchScroll();
        },
        { passive: true },
      );

      this.flydown.addEventListener(
        'scroll',
        () => {
          this._dismissKeyboardForSearchScroll();
        },
        { passive: true },
      );
    },

    _dismissKeyboardForSearchScroll() {
      if (!this.isModalOpen || !this.input || document.activeElement !== this.input) return;
      this.lastKeyboardDismissAt = Date.now();
      this.ignoreOutsideClickUntil = Date.now() + 150;
      this.input.blur();
    },

    openModal() {
      var focusRequestedAt = Date.now();
      if (!this.isModalOpen) {
        this.isModalOpen = true;
        this.ignoreOutsideClickUntil = Date.now() + SEARCH_OPENING_GUARD_MS;
      }
      document.body.classList.add('mh-search-modal-open');
      if (this.searchWrapper) this.searchWrapper.classList.add('mh-search--fullscreen');
      this._renderSearchHistory();
      this._renderRecentlyViewed();
      this.showFlydown();
      // Re-assert focus after DOM reflow so iOS keeps the keyboard open.
      requestAnimationFrame(() => {
        if (this.input && this.lastKeyboardDismissAt < focusRequestedAt) this.input.focus();
      });
    },

    closeModal() {
      this.isModalOpen = false;
      this.ignoreOutsideClickUntil = 0;
      document.body.classList.remove('mh-search-modal-open');
      if (this.searchWrapper) this.searchWrapper.classList.remove('mh-search--fullscreen');
      this.hideFlydown();
      this.input.blur();
    },

    showFlydown() {
      if (this.flydown) {
        this.flydown.classList.add('mh-search-flydown--visible');
        this.isOpen = true;
      }
    },

    hideFlydown() {
      if (this.flydown) {
        this.flydown.classList.remove('mh-search-flydown--visible');
        this.setLoading(false);
        this.isOpen = false;
      }
    },

    showQuickLinks() {
      if (!this.quickLinks) return;
      this.setLoading(false);
      if (this.resultsContainer) {
        this.resultsContainer.hidden = true;
        this.resultsContainer.innerHTML = '';
      }
      this.quickLinks.hidden = false;
      this.showFlydown();
    },

    hideQuickLinks() {
      if (this.quickLinks) this.quickLinks.hidden = true;
    },

    setLoading(isLoading) {
      if (this.flydown) {
        this.flydown.classList.toggle('mh-search-flydown--loading', isLoading);
      }
      if (this.placeholder) {
        this.placeholder.hidden = !isLoading;
      }
      if (this.resultsContainer) {
        this.resultsContainer.hidden = isLoading;
        if (isLoading) {
          this.resultsContainer.innerHTML = '';
        }
      }
    },

    // ── Search History ──────────────────────────────────────────────────

    _getPastSearches() {
      try {
        return JSON.parse(localStorage.getItem(PAST_SEARCH_KEY) || '[]');
      } catch (_) {
        return [];
      }
    },

    _saveSearch(query) {
      query = query.trim();
      if (!query) return;
      try {
        var list = this._getPastSearches().filter(function (s) {
          return s.toLowerCase() !== query.toLowerCase();
        });
        list.unshift(query);
        localStorage.setItem(PAST_SEARCH_KEY, JSON.stringify(list.slice(0, MAX_PAST_SEARCHES)));
      } catch (_) {}
    },

    _getSearchResultsUrl(query) {
      var form = this.input ? this.input.closest('form') : null;
      var action = form && form.getAttribute('action') ? form.getAttribute('action') : '/search';
      var base = window.location.origin && window.location.origin !== 'null' ? window.location.origin : 'https://example.test';
      var url = new URL(action, base);

      url.searchParams.set('q', String(query || '').trim());
      return url.pathname + url.search + url.hash;
    },

    _goToSearchResults(query) {
      query = String(query || '').trim();
      if (!query) return;

      this._saveSearch(query);
      window.location.href = this._getSearchResultsUrl(query);
    },

    _removeSearch(query) {
      try {
        var list = this._getPastSearches().filter(function (s) {
          return s.toLowerCase() !== query.toLowerCase();
        });
        localStorage.setItem(PAST_SEARCH_KEY, JSON.stringify(list));
      } catch (_) {}
    },

    _escHtml(s) {
      var d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML;
    },

    _renderSearchHistory() {
      var el = this.historyContainer;
      if (!el) return;

      var searches = this._getPastSearches();
      if (!searches.length) {
        el.hidden = true;
        return;
      }

      var self = this;
      var html =
        '<div class="mh-search-ql__header"><span class="mh-search-ql__title">Recent Searches</span>' +
        '<button type="button" class="mh-search-ql__clear" data-clear-search-history>Clear</button></div>' +
        '<div class="mh-search-ql__history-list">';

      searches.forEach(function (q) {
        var safe = self._escHtml(q);
        html +=
          '<div class="mh-search-ql__history-item">' +
          '<a class="mh-search-ql__history-link" href="/search?q=' +
          encodeURIComponent(q) +
          '">' +
          '<svg class="mh-search-ql__history-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' +
          '<span>' +
          safe +
          '</span>' +
          '</a>' +
          '<button type="button" class="mh-search-ql__history-remove" data-remove-search="' +
          safe +
          '" aria-label="Remove">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
          '</div>';
      });
      html += '</div>';

      el.innerHTML = html;
      el.hidden = false;

      el.querySelector('[data-clear-search-history]').addEventListener('click', function () {
        try {
          localStorage.removeItem(PAST_SEARCH_KEY);
        } catch (_) {}
        el.hidden = true;
      });

      el.querySelectorAll('[data-remove-search]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self._removeSearch(btn.getAttribute('data-remove-search'));
          self._renderSearchHistory();
        });
      });

      el.querySelectorAll('.mh-search-ql__history-link').forEach(function (link) {
        link.addEventListener('click', function () {
          var q = link.querySelector('span');
          if (q) self._saveSearch(q.textContent);
        });
      });
    },

    // ── Recently Viewed ─────────────────────────────────────────────────

    _getRecentlyViewedHandles() {
      try {
        var keys = Object.keys(localStorage);
        for (var i = 0; i < keys.length; i++) {
          if (keys[i].indexOf('pxu-recentlyViewed-') === 0) {
            var data = JSON.parse(localStorage.getItem(keys[i]) || '[]');
            if (Array.isArray(data) && data.length) {
              data.sort(function (a, b) {
                return (b.timestamp || 0) - (a.timestamp || 0);
              });
              return data
                .slice(0, MAX_RECENT_PRODUCTS)
                .map(function (item) {
                  return item.handle;
                })
                .filter(Boolean);
            }
          }
        }
      } catch (_) {}
      return [];
    },

    _renderRecentlyViewed() {
      var el = this.recentContainer;
      if (!el) return;

      var handles = this._getRecentlyViewedHandles();
      if (!handles.length) {
        el.hidden = true;
        return;
      }

      if (this.recentProductsRendered) {
        el.hidden = false;
        return;
      }

      el.innerHTML =
        '<span class="mh-search-ql__title">Recently Viewed</span>' +
        '<div class="mh-search-ql__recent-row">' +
        handles
          .map(function () {
            return (
              '<div class="mh-search-ql__popular-card mh-search-ql__popular-card--skeleton" aria-hidden="true">' +
              '<span class="mh-search-ql__popular-img mh-search-ql__popular-skel-media"></span>' +
              '<span class="mh-search-ql__popular-info">' +
              '<span class="mh-search-ql__popular-skel-line mh-search-ql__popular-skel-line--title"></span>' +
              '<span class="mh-search-ql__popular-skel-line mh-search-ql__popular-skel-line--price"></span>' +
              '</span></div>'
            );
          })
          .join('') +
        '</div>';
      el.hidden = false;

      var self = this;
      this._fetchProducts(handles).then(function (products) {
        if (!products.length) {
          el.hidden = true;
          return;
        }
        var row = el.querySelector('.mh-search-ql__recent-row');
        if (!row) return;
        row.innerHTML = products
          .map(function (p) {
            var title = (p.title || '')
              .replace(/^(?:FOR\s+)?PICK\s*UP\s+ONLY\s*\|\s*/i, '')
              .replace(/\s*\|\s*DOES NOT SHIP\s*$/i, '')
              .replace(/DOES NOT SHIP\s*$/i, '')
              .trim();
            var price = p.price ? self._formatMoney(p.price) : '';
            var img = p.featured_image ? p.featured_image.replace(/(\.(jpg|jpeg|png|webp|gif))/, '_120x120$1') : '';
            return (
              '<a class="mh-search-ql__popular-card" href="' +
              p.url +
              '">' +
              '<span class="mh-search-ql__popular-img">' +
              (img ? '<img src="' + img + '" alt="" width="60" height="60" loading="lazy">' : '') +
              '</span>' +
              '<span class="mh-search-ql__popular-info">' +
              '<span class="mh-search-ql__popular-name">' +
              self._escHtml(title) +
              '</span>' +
              (price ? '<span class="mh-search-ql__popular-price">' + price + '</span>' : '') +
              '</span></a>'
            );
          })
          .join('');
        self.recentProductsRendered = true;
      });
    },

    _fetchProducts(handles) {
      var loader = window.MrMapleProductJsonLoader;
      var promises = handles.map(function (handle) {
        if (!handle) return Promise.resolve(null);
        if (loader && typeof loader.load === 'function') {
          return loader.load(handle);
        }
        return fetch('/products/' + encodeURIComponent(handle) + '.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .catch(function () {
            return null;
          });
      });
      return Promise.all(promises).then(function (results) {
        return results.filter(Boolean);
      });
    },

    _formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    },

    // ── Predictive Search ───────────────────────────────────────────────

    async performPredictiveSearch(query) {
      if (!this.resultsContainer) return;

      var searchIntelligence = window.MrMapleSearchIntelligence || null;
      const escapeSearchLabel = (value) =>
        String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      const isJsonLikeSearchBody = (value) => {
        const trimmed = String(value || '').trim();
        return (
          trimmed === '{}' ||
          trimmed === '[]' ||
          (trimmed.charAt(0) === '{' && trimmed.charAt(trimmed.length - 1) === '}') ||
          (trimmed.charAt(0) === '[' && trimmed.charAt(trimmed.length - 1) === ']')
        );
      };

      this.hideQuickLinks();
      this.showFlydown();
      this.setLoading(true);

      try {
        const fetchResults = async (searchQuery) => {
          const response = await fetch(
            `/search/suggest?q=${encodeURIComponent(searchQuery)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last&section_id=predictive-search`,
            {
              credentials: 'same-origin',
              headers: { Accept: 'text/html' },
            },
          );

          if (!response.ok) throw new Error('Search failed');

          const contentType = response.headers.get('content-type') || '';
          if (contentType.toLowerCase().indexOf('application/json') !== -1) {
            throw new Error('Search returned JSON instead of HTML');
          }

          const html = await response.text();
          if (isJsonLikeSearchBody(html)) {
            throw new Error('Search returned JSON instead of HTML');
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          return {
            doc,
            resultsEl: doc.querySelector('.search-flydown__products-pages-posts'),
            luckyEl: doc.querySelector('[data-search-lucky-link]')?.closest('.search-flydown__lucky-wrap') || null,
          };
        };

        let activeQuery = query;
        let result = await fetchResults(activeQuery);

        if (searchIntelligence && (!result.resultsEl || !searchIntelligence.hasProductMatches(result.resultsEl))) {
          const suggestion = await searchIntelligence.findSuggestion(query);
          if (suggestion && suggestion.query) {
            activeQuery = suggestion.query;
            result = await fetchResults(activeQuery);
          }
        }

        if (result.resultsEl) {
          const correctedHasProducts = searchIntelligence
            ? searchIntelligence.hasProductMatches(result.resultsEl)
            : true;

          if (searchIntelligence) {
            searchIntelligence.rerankPredictiveResults(result.resultsEl, activeQuery);
          }

          this.resultsContainer.innerHTML = result.resultsEl.outerHTML;
          document.dispatchEvent(new CustomEvent('mm:cards:added'));

          if (searchIntelligence && activeQuery !== query) {
            searchIntelligence.injectPredictiveSuggestion(this.resultsContainer, {
              originalQuery: query,
              correctedQuery: activeQuery,
              emptyResults: !correctedHasProducts,
            });
          }

          if (activeQuery === query || correctedHasProducts) {
            this.resultsContainer.insertAdjacentHTML(
              'beforeend',
              `
              <a href="/search?q=${encodeURIComponent(activeQuery)}&options%5Bprefix%5D=last" class="mh-search-flydown__view-all">
                View all results for &ldquo;${escapeSearchLabel(activeQuery)}&rdquo;
              </a>
            `,
            );
          }

          if (result.luckyEl) {
            this.resultsContainer.insertAdjacentHTML('beforeend', result.luckyEl.outerHTML);
          }
        } else {
          const suggestion = searchIntelligence ? await searchIntelligence.findSuggestion(query) : null;
          const suggestionMarkup = suggestion
            ? `
            <p class="mh-search-flydown__hint">
              Closest catalog match: ${escapeSearchLabel(suggestion.query)}, but it is not available right now.
            </p>
          `
            : '';
          const luckyMarkup = result.luckyEl ? result.luckyEl.outerHTML : '';

          this.resultsContainer.innerHTML = `
            <div class="mh-search-flydown__no-results">
              <p>No available products found for &ldquo;${escapeSearchLabel(query)}&rdquo;</p>
              ${suggestionMarkup}
              <a href="/search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last" class="mh-search-flydown__browse-link">
                Search all results
              </a>
              <a href="/collections/all" class="mh-search-flydown__browse-link">Browse all products</a>
            </div>
            ${luckyMarkup}
          `;
        }
      } catch (error) {
        console.warn('Predictive search failed:', error);
        this.resultsContainer.innerHTML = `
          <a href="/search?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last" class="mh-search-flydown__view-all">
            Search for &ldquo;${escapeSearchLabel(query)}&rdquo;
          </a>
        `;
      } finally {
        this.setLoading(false);
      }
    },
  };

  /**
   * Bottom Nav Active State
   */
  const BottomNav = {
    init() {
      // Update cart count dynamically
      this.updateCartCount();

      // Listen for cart updates
      document.addEventListener('cart:updated', () => {
        this.updateCartCount();
      });
    },

    updateCartCount() {
      const badge = document.querySelector('[data-cart-count]');
      if (badge) {
        const count = parseInt(badge.textContent) || 0;
        badge.style.display = count > 0 ? 'flex' : 'none';
      }
    },
  };

  /**
   * Header Scroll Collapse Module
   * Collapses the top bar on scroll but keeps search accessible
   */
  const HeaderScroll = {
    header: null,
    searchInput: null,
    lastScrollY: 0,
    scrollThreshold: 50,
    expandThreshold: 60,
    isCollapsed: false,
    ticking: false,
    lastStateChangeAt: 0,
    cooldownMs: 350,

    init() {
      this.header = document.querySelector('[data-mobile-home-header]');
      this.searchInput = document.querySelector('[data-mobile-search-input]');

      if (!this.header) return;

      if (document.body.classList.contains('template-search')) {
        this.header.classList.remove('mh-header--collapsed');
        this.isCollapsed = false;
        return;
      }

      window.addEventListener('scroll', () => this.onScroll(), {
        passive: true,
      });

      if (this.searchInput) {
        this.searchInput.addEventListener('focus', () => {});
      }
    },

    onScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
        });
        this.ticking = true;
      }
    },

    handleScroll() {
      const currentScrollY = window.scrollY;
      const now = Date.now();
      const inCooldown = now - this.lastStateChangeAt < this.cooldownMs;

      if (inCooldown) {
        this.lastScrollY = currentScrollY;
        return;
      }

      const scrollDelta = this.lastScrollY - currentScrollY;

      if (currentScrollY > this.scrollThreshold && currentScrollY > this.lastScrollY) {
        this.collapse();
      } else if (currentScrollY < this.scrollThreshold || scrollDelta >= this.expandThreshold) {
        this.expand();
      }

      this.lastScrollY = currentScrollY;
    },

    collapse() {
      if (!this.isCollapsed && this.header) {
        this.header.classList.add('mh-header--collapsed');
        this.isCollapsed = true;
        this.lastStateChangeAt = Date.now();
      }
    },

    expand() {
      if (this.isCollapsed && this.header) {
        this.header.classList.remove('mh-header--collapsed');
        this.isCollapsed = false;
        this.lastStateChangeAt = Date.now();
      }
    },
  };

  /**
   * Initialize all modules
   */
  var PRODUCT_IMAGE_CONTAINER_SELECTORS = [
    '[data-mobile-product-grid]',
    '.featured-collection__content[data-mobile-layout="grid"]',
    '.product-recently-viewed__content',
    '[data-trending-container]',
  ];

  function init() {
    if (!isMobile()) return;

    // Add body class for mobile home
    if (document.body.classList.contains('template-index')) {
      document.body.classList.add('mobile-home-active');
    }

    ZoneSelector.init();
    PromoCarousel.init();
    CategoryFilter.init();
    WishlistButtons.init();
    PersonalizedRecommendations.init();
    SearchHandler.init();
    BottomNav.init();
    HeaderScroll.init();

    loadLazyImagesInContainers(document);

    // Some sections finish layout after init; run a second pass so canvases clear reliably.
    setTimeout(function () {
      loadLazyImagesInContainers(document);
    }, 260);
  }

  function loadLazyImagesInContainers(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    var seen = [];

    PRODUCT_IMAGE_CONTAINER_SELECTORS.forEach(function (selector) {
      var containers = root.querySelectorAll(selector);
      for (var i = 0; i < containers.length; i++) {
        var container = containers[i];
        if (seen.indexOf(container) !== -1) continue;
        seen.push(container);
        loadLazyImages(container);
      }
    });
  }

  function loadLazyImages(container) {
    if (!container) return;
    var imgs = container.querySelectorAll('img[data-rimg="lazy"]');
    if (!imgs.length) return;

    container.dispatchEvent(new CustomEvent('theme:rimg:watch', { bubbles: true, cancelable: true }));

    setTimeout(function () {
      imgs.forEach(function (img) {
        if (img.getAttribute('data-rimg') === 'lazy' && img.src) {
          img.setAttribute('srcset', '');
          img.removeAttribute('srcset');
          img.setAttribute('data-rimg', 'loaded');
        }
      });
    }, 200);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on resize (debounced)
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (isMobile() && !document.body.classList.contains('mobile-home-active')) {
        init();
      }
    }, 250);
  });
})();
